// pages/CustomerRelationship.js
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import ListIcon from "@mui/icons-material/List";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GridViewIcon from "@mui/icons-material/GridView";
import buxferService from '../services/buxfer.service';
import amexOffersService from '../services/amex_offers.service';
import customerDashboardService from '../services/customer_dashboard.service';
import { storageHandler } from '../utils/storage_handler';
import jwt_decode from 'jwt-decode';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import BankList from "../components/BankList";
import CreditUtilization from "../components/CreditUtilization";
import AccountBalancesOverview from "../components/AccountBalancesOverview";
import UpcomingPaymentsPanel from "../components/UpcomingPaymentsPanel";
import AccountRenewalsPanel from "../components/AccountRenewalsPanel";
import CreditProfilePanel from "../components/CreditProfilePanel";
import OffersOverviewPanel from "../components/OffersOverviewPanel";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ width: "100%" }}>
      {value === index && children}
    </div>
  );
}

export default function CustomerRelationshipPage() {
  // Get record_id from route params
  //const { record_id: routeRecordId } = useParams();
  const [record_id, setRecordId] = useState('');
  const [tab, setTab] = useState(0);
  const [viewMode, setViewMode] = useState("summary");

  // Convert all static data to useState
  const [customerProfile, setCustomerProfile] = useState(null);
  const [amexOffers, setAmexOffers] = useState([]);

  // Fetch customer profile from backend when filters or record_id change
  useEffect(() => {
    const token = storageHandler.localStorageGet('token');
    let currentRecordId = '';

    if (token) {
      try {
        const decoded = jwt_decode(token);
        if (decoded.usertype === 2 && decoded.record_id) {
          currentRecordId = decoded.record_id;
          setRecordId(currentRecordId);
        }
      } catch (error) {
        console.error("Failed to decode token:", error);
      }
    }

    console.log("Decoded currentRecordId:", currentRecordId); // Log the decoded currentRecordId

    const fetchData = async () => {
      if (!currentRecordId) {
        console.log("No record_id available to fetch data.");
        return;
      }

      try {
        setRecordId(currentRecordId);
        const [dashboardData, profileData, offersData] = await Promise.all([
          customerDashboardService.getDashboardData(currentRecordId),
          buxferService.getCustomerProfile(currentRecordId, { search, bank, accountType, status }),
          amexOffersService.getOffersForCustomer(currentRecordId),
        ]);

        if (dashboardData && dashboardData.success) {
          setAccountBalancesSummary(dashboardData.accountBalancesSummary);
          setAccountBalancesGroups(dashboardData.accountBalancesGroups);
          //setCreditUtilizationData(dashboardData.creditUtilizationData);
          //setUpcomingPaymentsData(dashboardData.upcomingPaymentsData);
          //setAccountRenewalsData(dashboardData.accountRenewalsData);
        }

        if (profileData && profileData.success && profileData.profile) {
          setCustomerProfile({
            record_id: profileData.profile.record_id || "",
            module_id: profileData.profile.module_id || "",
            namespace_id: profileData.profile.namespace_id || "",
            created_at: profileData.profile.created_at || "",
            created_by: profileData.profile.created_by || "",
            control_level: profileData.profile.control_level || "",
            name: profileData.profile.full_name || "",
            email: profileData.profile.cc_email || "",
            personal_phone: profileData.profile.personal_phone || "",
            home_address: profileData.profile.home_address || "",
            newly_assigned_phone: profileData.profile.newly_assigned_phone || "",
            status: profileData.profile.status || "",
            programs: profileData.profile.programs || "",
            sms_forwarding: profileData.profile.sms_forwarding || "",
            can_ship_to_home: profileData.profile.can_ship_to_home || "",
            pc_assigned: profileData.profile.pc_assigned || "",
            notes: profileData.profile.notes || "",
            customerId: profileData.profile.record_id || "",
          });
        }

        if (offersData && offersData.success && Array.isArray(offersData.offers)) {
          setAmexOffers(offersData.offers);
        } else {
          setAmexOffers([]);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setCustomerProfile(null);
        setAmexOffers([]);
      }
    };

    fetchData();
  }, [record_id, search, bank, accountType, status]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (record_id === "") {
          return;
        }

        const response = await customerDashboardService.getDashboardData(record_id);
        console.log(response)
        if (response.success) {
          setAccountBalancesGroups(response.accountBalances.groups);
        } else {
          console.error("Failed to fetch dashboard data:", response);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, [record_id]);

  const [accountBalancesSummary, setAccountBalancesSummary] = useState({
    depositsTotal: 48500,
    depositsCount: 3,
    creditUsed: 16300,
    creditCardsCount: 9,
    availableCredit: 157700,
  });

  const [accountBalancesGroups, setAccountBalancesGroups] = useState({
    checking: [
      { bank: "Chase Bank", masked: "****1234", amount: 15000, apy: "0.01%" },
      { bank: "US Bank", masked: "****9876", amount: 8500, apy: "0.01%" },
    ],
    savings: [
      { bank: "Chase Bank", masked: "****5678", amount: 25000, apy: "2.5%" },
    ],
    credits: [
      { product: "Gold Card", bank: "American Express", masked: "****1006", used: 4200, limit: 30000 },
      { product: "Blue Cash Preferred", bank: "American Express", masked: "****2015", used: 2800, limit: 18000 },
    ],
  });

  const [accountBalancesColors, setAccountBalancesColors] = useState({
    positive: "success.main",
    negative: "error.main",
    creditUsedAmount: "error.main",
  });

  const [bankListData, setBankListData] = useState([
    {
      bankName: "American Express",
      relationship: "Premium Relationship",
      relationshipColor: "blue",
      badgeText: "Active",
      badgeColor: "#C8E6C9",
      totalBalance: "$0 Total Balance",
      creditCards: "3 Credit Cards",
      accountTypes: [
        { count: 2, label: "Credit" },
        { count: 1, label: "Charge" },
      ],
      utilization: { percent: "8", value: "$98,000" },
      accountDetails: [
        {
          name: "Amex Gold",
          owed: "$0",
          masked: "•••• 1234",
          limit: "$25,000",
          utilized: "5%",
          apr: "15.99%",
        },
        {
          name: "Amex Platinum",
          owed: "$0",
          masked: "•••• 5678",
          limit: "$50,000",
          utilized: "10%",
          apr: "19.99%",
        },
      ],
      lastActivity: "1/8/2025",
    },
    {
      bankName: "Chase Bank",
      relationship: "Primary Relationship",
      relationshipColor: "blue",
      badgeText: "Active",
      badgeColor: "#C8E6C9",
      totalBalance: "$40,000 Total Balance",
      creditCards: "5 Accounts",
      accountTypes: [
        { count: 3, label: "Credit" },
        { count: 2, label: "Checking" },
      ],
      utilization: { percent: "12", value: "$40,000" },
      accountDetails: [
        {
          name: "Sapphire Preferred",
          owed: "$485",
          masked: "•••• 4321",
          limit: "$20,000",
          utilized: "2%",
          apr: "17.99%",
        },
        {
          name: "Amazon Prime Rewards",
          owed: "$95",
          masked: "•••• 8765",
          limit: "$10,000",
          utilized: "1%",
          apr: "16.99%",
        },
      ],
      lastActivity: "2/1/2025",
    },
    {
      bankName: "US Bank",
      relationship: "Standard Relationship",
      relationshipColor: "grey",
      badgeText: "Active",
      badgeColor: "#C8E6C9",
      totalBalance: "$33,500 Total Balance",
      creditCards: "2 Accounts",
      accountTypes: [
        { count: 1, label: "Credit" },
        { count: 1, label: "Checking" },
      ],
      utilization: { percent: "5", value: "$8,000" },
      accountDetails: [
        {
          name: "Altitude Go Visa",
          owed: "$85",
          masked: "•••• 5678",
          limit: "$8,000",
          utilized: "1%",
          apr: "15.99%",
        },
      ],
      lastActivity: "3/1/2025",
    },
  ]);

  const [creditUtilizationData, setCreditUtilizationData] = useState({
    overall: {
      utilization: 9,
      used: 16300,
      limit: 174000,
    },
    cards: [
      {
        title: "Blue Cash Preferred",
        subtitle: "****2015",
        progressLabel: "16% utilization",
        progressValue: 16,
        topRows: [
          { label: "APR", value: "18.9%" },
          { label: "Used", value: "$2,800" },
          { label: "Limit", value: "$18,000" },
        ],
        bottomRows: [
          { label: "Status", value: "Good" },
          { label: "Available", value: "$15,200" },
        ],
      },
      {
        title: "Platinum Card",
        subtitle: "****3024",
        progressLabel: "2% utilization",
        progressValue: 2,
        topRows: [
          { label: "APR", value: "21.9%" },
          { label: "Used", value: "$950" },
          { label: "Limit", value: "$50,000" },
        ],
        bottomRows: [
          { label: "Status", value: "Good" },
          { label: "Available", value: "$49,050" },
        ],
      },
    ],
  });

  const [upcomingPaymentsData, setUpcomingPaymentsData] = useState({
    summaryItems: [
      { label: "Total Due (30 days)", value: "$3,115" },
      { label: "AutoPay Status", value: "4 of 8 payments" },
    ],
    payments: [
      { title: "US Bank", subtitle: "Cash+ Visa Signature", amount: "$225", status: "Overdue", rightMeta: "217 days overdue", statusColor: "error" },
      { title: "Chase Bank", subtitle: "Sapphire Preferred", amount: "$485", status: "Due Soon", rightMeta: "-214 days", statusColor: "warning" },
      { title: "American Express", subtitle: "Gold Card", amount: "$680", status: "Due Soon", rightMeta: "-210 days", statusColor: "warning" },
      { title: "US Bank", subtitle: "Altitude Go Visa", amount: "$85", status: "Due Soon", rightMeta: "-207 days", statusColor: "warning" },
      { title: "American Express", subtitle: "Blue Cash Preferred", amount: "$420", status: "Due Soon", rightMeta: "-204 days", statusColor: "warning" },
      { title: "Chase Bank", subtitle: "Amazon Prime Rewards", amount: "$95", status: "Due Soon", rightMeta: "-202 days", statusColor: "warning" },
    ],
    buttons: [
      { label: "Make Payments" },
      { label: "Set Up AutoPay" },
      { label: "View All" },
    ],
  });

  const [accountRenewalsData, setAccountRenewalsData] = useState({
    summaryItems: [
      { label: "Next 90 Days", value: "7 renewals" },
      { label: "Total Annual Fees", value: "$1,135/year" },
      { label: "Auto-Renewal", value: "6 of 8 accounts" },
    ],
    renewals: [
            { title: "Freedom Unlimited", subtitle: "Chase Bank", amount: "No Fee", status: "Action Needed", rightMeta: "Renews 2/15/2025", statusColor: "error" },
            { title: "Altitude Go Visa", subtitle: "US Bank", amount: "No Fee", status: "Renewing Soon", rightMeta: "Renews 3/18/2025", statusColor: "warning" },
            { title: "Sapphire Preferred", subtitle: "Chase Bank", amount: "$95/year", status: "Renewing Soon", rightMeta: "Renews 4/1/2025", statusColor: "error" },
            { title: "Blue Cash Preferred", subtitle: "American Express", amount: "$95/year", status: "Renewing Soon", rightMeta: "Renews 5/12/2025", statusColor: "error" },
            { title: "Cash+ Visa Signature", subtitle: "US Bank", amount: "No Fee", status: "Renewing Soon", rightMeta: "Renews 7/1/2025", statusColor: "error" },
            { title: "Amazon Prime Rewards", subtitle: "Chase Bank", amount: "No Fee", status: "Renewing Soon", rightMeta: "Renews 8/10/2025", statusColor: "warning" },
        ],
        buttons: [
            { label: "Review Renewals" },
            { label: "Set Auto-Renewal" },
            { label: "View All" },
        ],
    });
    const creditProfileData = {
        summary: {
            score: 750,
            rating: "Very Good",
            change: "+0",
            scale: { color: "#4caf50" }
        },
        trend: {
            labels: ["Jul 24", "Aug 24", "Sep 24", "Oct 24", "Nov 24", "Dec 24", "Jan 25"],
            values: [725, 730, 735, 740, 745, 748, 750]
        },
        factors: [
            { name: "Payment History", impact: 35, score: 100, rating: "Excellent", color: "#4caf50" },
            { name: "Credit Utilization", impact: 30, score: 100, rating: "Excellent", color: "#4caf50" },
            { name: "Credit Age", impact: 15, score: 85, rating: "Very Good", color: "#ffb300" },
            { name: "Credit Mix", impact: 10, score: 100, rating: "Excellent", color: "#4caf50" },
            { name: "Credit Inquiries", impact: 10, score: 95, rating: "Excellent", color: "#4caf50" },
        ],
        utilization: {
            percent: 9,
            balance: 16300,
            limit: 174000,
            available: 157700
        },
        accounts: [
            { bank: "American Express", balance: 4200, limit: 30000, apr: "18.9%", opened: "11/20/2019", status: "On Time" },
            { bank: "American Express", balance: 2100, limit: 30000, apr: "18.9%", opened: "5/12/2021", status: "On Time" },
            { bank: "American Express", balance: 600, limit: 30000, apr: "18.9%", opened: "5/12/2021", status: "On Time" },
            { bank: "Chase Bank", balance: 1000, limit: 30000, apr: "18.9%", opened: "2/15/2021", status: "On Time" },
            { bank: "Chase Bank", balance: 1000, limit: 30000, apr: "18.9%", opened: "2/15/2021", status: "On Time" },
            { bank: "Chase Bank", balance: 7400, limit: 24000, apr: "18.9%", opened: "2/15/2021", status: "On Time" }
        ]
    };


  // Filter state
  const [search, setSearch] = useState("");
  const [bank, setBank] = useState("All Banks");
  const [accountType, setAccountType] = useState("All Types");
  const [status, setStatus] = useState("All Status");
  // Example options (replace with backend data later)
  const bankOptions = ["All Banks", "American Express", "Chase Bank", "US Bank"];
  const accountTypeOptions = ["All Types", "Credit", "Checking", "Savings", "Charge", "Travel", "Cashback", "Premium", "Business"];
  const statusOptions = ["All Status", "Active", "Suspended", "Canceling", "Low Credit", "Cancelled", "Cleared", "Pending", "Not Managed", "NO INFO"];
  // Result count placeholder
  const resultCount = 3; // Replace with dynamic count
  const accountCount = 12; // Replace with dynamic count

  // Clear filters handler
  const handleClearFilters = () => {
    setSearch("");
    setBank("All Banks");
    setAccountType("All Types");
    setStatus("All Status");
  };

  const handleChangeTab = (event, newValue) => setTab(newValue);
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) setViewMode(newMode);
  };

  // Empty state toggle
  const [showEmptyChips, setShowEmptyChips] = useState(false);

  return (
    <Box p={3}>
      {/* Header & Subtitle */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Customer Relationship Management - Single Customer
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Comprehensive banking relationship overview for Sarah Johnson
      </Typography>
      {/* Temporary toggle for empty state chips */}
      <Box mb={2}>
        <button
          onClick={() => setShowEmptyChips(v => !v)}
          style={{
            borderRadius: 8,
            padding: "4px 12px",
            border: "1px solid #ddd",
            background: "#fff",
            cursor: "pointer",
            fontSize: "0.95rem",
            marginRight: 8,
          }}
        >
          Toggle Empty State Chips
        </button>
        {showEmptyChips && (
          <Box display="flex" gap={1} mt={1}>
            <Chip label="Credit Score Unavailable" color="error" icon={<span style={{fontWeight:'bold'}}>⚠️</span>} />
            <Chip label="No Credit Utilization Data" color="error" icon={<span style={{fontWeight:'bold'}}>⚠️</span>} />
          </Box>
        )}
      </Box>

      {/* Search & Filter Grouped UI */}
      <Box sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: 1,
        p: 3,
        mb: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="subtitle1" fontWeight={500} mb={1}>
          Search & Filter
        </Typography>
        {/* Search bar */}
        <Box mb={2}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by bank name, account type, card number, or product name..."
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "12px",
              border: "1px solid #ddd",
              fontSize: "1rem",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </Box>
        {/* Dropdowns + Actions */}
        <Box display="flex" gap={2} alignItems="center" mb={1}>
          <select value={bank} onChange={e => setBank(e.target.value)} style={{ borderRadius: 12, padding: "8px 16px", minWidth: 140 }}>
            {bankOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <select value={accountType} onChange={e => setAccountType(e.target.value)} style={{ borderRadius: 12, padding: "8px 16px", minWidth: 140 }}>
            {accountTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ borderRadius: 12, padding: "8px 16px", minWidth: 140 }}>
            {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <Box flexGrow={1} />
          <button onClick={handleClearFilters} style={{ borderRadius: 12, padding: "8px 20px", border: "1px solid #ddd", background: "#fff", cursor: "pointer" }}>
            &#10005; Clear Filters
          </button>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Showing {resultCount} banks with {accountCount} accounts
        </Typography>
      </Box>

      {/* Profile Card */}
      <Card sx={{ borderRadius: 2, boxShadow: 2, mb: 3 }}>
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Left side - profile */}
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 56, height: 56 }}>{customerProfile && customerProfile.name ? customerProfile.name.split(' ').map(n => n[0]).join('') : ''}</Avatar>
            <Box>
              <Typography variant="h6">{customerProfile && customerProfile.name ? customerProfile.name : ''}</Typography>
              <Typography variant="body2" color="text.secondary">
                {customerProfile && customerProfile.email ? customerProfile.email : ''}
              </Typography>
              <Box display="flex" gap={1} mt={1}>
                <Chip label={`Credit Score: ${customerProfile && customerProfile.creditScore ? customerProfile.creditScore : ''}`} size="small" />
                <Chip label={`Risk Level: ${customerProfile && customerProfile.riskLevel ? customerProfile.riskLevel : ''}`} color="success" size="small" />
              </Box>
            </Box>
          </Box>

          {/* Right side - relationship value */}
          <Box textAlign="right" flexGrow={1}>
            <Chip label={`Customer ID: ${customerProfile && customerProfile.customerId ? customerProfile.customerId : ''}`} variant="outlined" />
            <Typography variant="h6" mt={1}>
              Total Relationship Value
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {customerProfile && customerProfile.relationshipValue ? `$${customerProfile.relationshipValue}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {customerProfile && customerProfile.accounts ? `${customerProfile.accounts} accounts` : ''}
              {customerProfile && customerProfile.creditLimit ? ` • $${customerProfile.creditLimit} credit limit` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {customerProfile && customerProfile.utilization ? `${customerProfile.utilization}% utilization across all cards` : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* View Mode Toggle */}
      <Box mb={2}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            gap: 1,
            "& .MuiToggleButton-root": {
              border: "1px solid",
              borderColor: "rgba(255,255,255,0.2)",
              borderRadius: "8px",
              textTransform: "none",
              px: 2,
              gap: 0.5,
              display: "flex",
              alignItems: "center",
              color: "rgba(255,255,255,0.8)",
              "&.Mui-selected": {
                backgroundColor: "#fff",
                color: "#000",
                borderColor: "#fff",
                "&:hover": {
                  backgroundColor: "#e5e5e5",
                },
              },
            },
          }}
        >
          <ToggleButton value="summary">
            <ListIcon sx={{ fontSize: 16 }} />
            Summary
          </ToggleButton>
          <ToggleButton value="detailed">
            <TrendingUpIcon sx={{ fontSize: 16 }} />
            Detailed
          </ToggleButton>
          <ToggleButton value="grid">
            <GridViewIcon sx={{ fontSize: 16 }} />
            Grid
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chip-style Full Width Tabs */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          mb: 2,
          p: 0.5,
          // Add shadow and background for tabs
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          sx={{
            bgcolor: "grey.100",
            borderRadius: 2,
            boxShadow: 1,
            '& .MuiTab-root': {
              textTransform: 'none',
              borderRadius: '20px',
              mx: 1,
              my: 1,
              bgcolor: 'grey.100',
              color: 'black',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            },
            '& .Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white !important',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Banking Details" />
          <Tab label="Credit Profile" />
          <Tab label="Offers & Opportunities" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
          <BankList data={bankListData} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              width: "100%",
            }}
          >
            <CreditUtilization overall={creditUtilizationData.overall} cards={creditUtilizationData.cards} />
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                p: 2,
                boxShadow: 1,
              }}
            >
              <AccountBalancesOverview
                summary={accountBalancesSummary}
                groups={accountBalancesGroups}
                colors={accountBalancesColors}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              width: "100%",
            }}
          >
            <UpcomingPaymentsPanel
              summaryItems={upcomingPaymentsData.summaryItems}
              payments={upcomingPaymentsData.payments}
              buttons={upcomingPaymentsData.buttons}
            />
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                p: 2,
                boxShadow: 1,
              }}
            >
              <AccountRenewalsPanel
                summaryItems={accountRenewalsData.summaryItems}
                renewals={accountRenewalsData.renewals}
                buttons={accountRenewalsData.buttons}
              />
            </Box>
          </Box>
        </Box>
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 2,
              p: 2,
              boxShadow: 1,
              width: "100%",
            }}
          >
            <AccountBalancesOverview
              summary={accountBalancesSummary}
              groups={accountBalancesGroups}
              colors={accountBalancesColors}
              showActions={true}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              width: "100%",
            }}
          >
            <UpcomingPaymentsPanel
              summaryItems={upcomingPaymentsData.summaryItems}
              payments={upcomingPaymentsData.payments}
              buttons={upcomingPaymentsData.buttons}
              paymentSummaryByBank={[
                {
                  bank: "American Express",
                  payments: 3,
                  autopay: 2,
                  amount: 2300,
                  ratio: "2/3 autopay",
                },
                {
                  bank: "Chase Bank",
                  payments: 3,
                  autopay: 2,
                  amount: 730,
                  ratio: "2/3 autopay",
                },
                {
                  bank: "US Bank",
                  payments: 1,
                  autopay: 0,
                  amount: 85,
                  ratio: "0/1 autopay",
                },
              ]}
            />
            <AccountRenewalsPanel
              summaryItems={accountRenewalsData.summaryItems}
              renewals={[
                {
                  title: "Freedom Unlimited",
                  subtitle: "Chase Bank",
                  amount: "No Fee",
                  status: "Action Needed",
                  rightMeta: "Renews 2/15/2025",
                  statusColor: "error",
                  overdue: true,
                  benefits: ["1.5% cash back on all purchases", "No annual fee"],
                },
                {
                  title: "Amazon Prime Rewards",
                  subtitle: "Chase Bank",
                  amount: "No Fee",
                  status: "Renewing Soon",
                  rightMeta: "Renews 8/10/2025",
                  statusColor: "warning",
                  overdue: true,
                  benefits: ["5% back on Amazon", "2% back on gas", "No foreign transaction fees"],
                },
                {
                  title: "Platinum Card",
                  subtitle: "American Express",
                  amount: "$695/year",
                  status: "Review Needed",
                  rightMeta: "Renews 9/5/2025",
                  statusColor: "error",
                  days: 15,
                  benefits: ["5x points on flights", "5x points on hotels", "Airport lounges", "+1 more"],
                },
                {
                  title: "Gold Card",
                  subtitle: "American Express",
                  amount: "$250/year",
                  status: "Upcoming",
                  rightMeta: "Renews 11/20/2025",
                  statusColor: "info",
                  days: 91,
                  benefits: ["4x points on dining", "4x points on groceries", "Airport lounges"],
                },
              ]}
              buttons={accountRenewalsData.buttons}
              detailed={true}
            />
          </Box>
        </Box>
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <CreditProfilePanel data={creditProfileData} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <OffersOverviewPanel data={{
          availableOffers: amexOffers.length,
          potentialValue: amexOffers.reduce((sum, offer) => sum + (parseFloat(offer.value) || 0), 0),
          expiringSoon: amexOffers.filter(o => o.expiration && Number(o.expiration) < 30).length,
          recommendations: amexOffers.slice(0, 2).map(offer => ({
            title: offer.offerName,
            source: offer.cardName,
            type: offer.offerType,
            badge: offer.expiration && Number(offer.expiration) < 30 ? 'Expires Soon' : 'Available',
            badgeColor: offer.expiration && Number(offer.expiration) < 30 ? 'warning' : 'info',
            value: offer.value || '',
            valueColor: 'success.main',
            description: offer.offerBody,
            expiration: offer.expiration,
          })),
          groupedOffers: Object.entries(
            amexOffers.reduce((groups, offer) => {
              const bank = offer.cardName || 'Other';
              if (!groups[bank]) groups[bank] = [];
              groups[bank].push({
                title: offer.offerName,
                type: offer.offerType,
                badge: offer.expiration && Number(offer.expiration) < 30 ? 'Expires Soon' : 'Available',
                badgeColor: offer.expiration && Number(offer.expiration) < 30 ? 'warning' : 'info',
                value: offer.value || '',
                valueColor: 'success.main',
                description: offer.offerBody,
                requirements: [],
                creditScore: '',
                expiration: offer.expiration,
                annualFee: '',
              });
              return groups;
            }, {})
          ).map(([bank, offers]) => ({ bank, offers })),
        }} />
      </TabPanel>
    </Box>
  );
}
